// AIDEV-NOTE: Browser-safe PHI redaction shared by client and server log transports.
// Keep this module free of server-only imports so it can be bundled for the browser.

// Substrings (case-insensitive) of object keys whose VALUES must never leave the app.
const SENSITIVE_KEY_PATTERNS = [
    "transcript",
    "transcription",
    "utterance",
    "diariz",
    "prompt",
    "completion",
    "rawreport",
    "raw_report",
    "report",
    "feedback",
    "soap",
    "meditation",
    "notes",
    "note_body",
    "body",
    "content",
    "messagebody",
    "message_body",
    "fullname",
    "full_name",
    "firstname",
    "first_name",
    "lastname",
    "last_name",
    "clientname",
    "client_name",
    "practitionername",
    "practitioner_name",
    "participantname",
    "participant_name",
    "displayname",
    "display_name",
    "email",
    "phone",
    "address",
    "token",
    "signedurl",
    "signed_url",
    "textpreview",
    "rawpart",
    "rawresponse",
    "partname",
    "mergedpartname",
    "groupname",
] as const;

// Generic clinical keys use suffix matching so diagnostics such as
// summaryToolPresent/topicCount/snippetCount remain available.
const SENSITIVE_KEY_SUFFIX_PATTERNS = ["snippet", "summary", "topic"] as const;

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Strip the query string of any http(s) URL — signed media URLs carry
// X-Amz-Signature / ?token= JWTs that grant access to raw session audio.
const URL_QUERY_RE = /(https?:\/\/[^\s?"']+)\?[^\s"']*/g;

const REDACTED = "[redacted]";
const REDACTED_EMAIL = "[redacted-email]";
const MAX_DEPTH = 6;
const MAX_STRING = 2000;

function keyIsSensitive(key: string): boolean {
    const lower = key.toLowerCase();
    return (
        SENSITIVE_KEY_PATTERNS.some((pattern) => lower.includes(pattern)) ||
        SENSITIVE_KEY_SUFFIX_PATTERNS.some((pattern) => lower.endsWith(pattern))
    );
}

// Strip credential-bearing query strings from URLs (signed S3 / Supabase URLs).
export function redactUrlCredentials(value: string): string {
    return value.replace(URL_QUERY_RE, "$1?[redacted]");
}

// Mask identifiers inside a free-text string (e.g. the log message itself).
export function redactString(value: string): string {
    const masked = redactUrlCredentials(value).replace(
        EMAIL_RE,
        REDACTED_EMAIL,
    );
    return masked.length > MAX_STRING
        ? `${masked.slice(0, MAX_STRING)}…[truncated]`
        : masked;
}

// Recursively redact a structured payload (Sentry `extra`, Datadog log params).
export function redactSensitive<T>(
    input: T,
    depth = 0,
    seen = new WeakSet(),
): T {
    if (input == null) {
        return input;
    }

    if (typeof input === "string") {
        const trimmed = input.trim();
        if (
            depth < MAX_DEPTH &&
            ((trimmed.startsWith("{") && trimmed.endsWith("}")) ||
                (trimmed.startsWith("[") && trimmed.endsWith("]")))
        ) {
            try {
                const parsed = JSON.parse(trimmed) as unknown;
                if (parsed !== null && typeof parsed === "object") {
                    return JSON.stringify(
                        redactSensitive(parsed, depth + 1, seen),
                    ) as unknown as T;
                }
            } catch {
                // Not valid JSON; apply normal string masking below.
            }
        }
        return redactString(input) as unknown as T;
    }

    if (typeof input !== "object") {
        return input;
    }

    if (depth >= MAX_DEPTH) {
        return REDACTED as unknown as T;
    }

    if (seen.has(input as object)) {
        return "[circular]" as unknown as T;
    }
    seen.add(input as object);

    if (Array.isArray(input)) {
        return input.map((item) =>
            redactSensitive(item, depth + 1, seen),
        ) as unknown as T;
    }

    // Preserve Errors as readable, scrubbed shapes.
    if (input instanceof Error) {
        return {
            name: input.name,
            message: redactString(input.message),
        } as unknown as T;
    }

    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(input as Record<string, unknown>)) {
        if (keyIsSensitive(key)) {
            out[key] = REDACTED;
        } else {
            out[key] = redactSensitive(val, depth + 1, seen);
        }
    }
    return out as unknown as T;
}

export function redactLogTransportParams<
    T extends {
        messages: unknown[];
        data?: unknown;
    },
>(params: T): T {
    return {
        ...params,
        messages: redactSensitive(params.messages),
        data: params.data ? redactSensitive(params.data) : params.data,
    };
}

interface ScrubbableSentryBreadcrumb {
    message?: string;
    data?: Record<string, unknown>;
}

interface ScrubbableSentryEvent {
    message?: string;
    extra?: Record<string, unknown>;
    contexts?: Record<string, unknown>;
    request?: {
        data?: unknown;
        cookies?: unknown;
        headers?: unknown;
    };
    exception?: { values?: Array<{ value?: string }> };
    breadcrumbs?: ScrubbableSentryBreadcrumb[];
}

export function scrubSentryBreadcrumb<B extends ScrubbableSentryBreadcrumb>(
    breadcrumb: B,
): B {
    if (typeof breadcrumb.message === "string") {
        breadcrumb.message = redactString(breadcrumb.message);
    }
    if (breadcrumb.data) {
        breadcrumb.data = redactSensitive(breadcrumb.data);
    }
    return breadcrumb;
}

export function scrubSentryEvent<E extends ScrubbableSentryEvent>(event: E): E {
    if (typeof event.message === "string") {
        event.message = redactString(event.message);
    }
    if (event.extra) {
        event.extra = redactSensitive(event.extra);
    }
    if (event.contexts) {
        event.contexts = redactSensitive(event.contexts);
    }
    if (event.request) {
        // AIDEV-NOTE: HIPAA — Sentry may supply request bodies as opaque JSON strings.
        // Drop bodies/headers/cookies entirely; allowlisting them is safer than parsing.
        delete event.request.data;
        delete event.request.headers;
        delete event.request.cookies;
    }
    if (event.exception?.values) {
        for (const value of event.exception.values) {
            if (typeof value.value === "string") {
                value.value = redactString(value.value);
            }
        }
    }
    if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) =>
            scrubSentryBreadcrumb(breadcrumb),
        );
    }
    return event;
}
